import { Action, BasicEditingSupport } from 'common/table/editing-support.interface';
import { AggregatedProduct, ApiProductRevision, ApiV1Service } from 'api';
import { computed, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AddProductComponent } from 'component/add/add-product.component';
import { AddRevisionComponent } from 'component/details/add/add-revision.component';
import { AppService } from 'app/app.service';
import { BillingStatusService } from 'common/billing/billing-status.service';

export class ProductsEditingSupport extends BasicEditingSupport<AggregatedProduct> {
  private readonly appService = inject(AppService);
  private apiService = inject(ApiV1Service);
  private readonly billingStatusService = inject(BillingStatusService);

  private addDisabled = computed(() => {
    return this.billingStatusService.status()?.isProductCreationPossible === false;
  });

  OPEN_ALL_ACTION: Action<AggregatedProduct> = {
    id: 'openAll',
    scopes: ['contextMenu'],
    icon: 'list',
    labelMessageKey: 'products.openProduct',
    disabled: (item?: AggregatedProduct) => !item?.latestRevisionId,
    on: (item: AggregatedProduct) => this.openAllRevisions(item!.id),
  };

  ADD_PRODUCT_ACTION: Action<AggregatedProduct> = {
    id: 'addProduct',
    scopes: ['menu'],
    icon: 'add',
    labelMessageKey: 'products.add',
    disabled: () => this.addDisabled(),
    on: async () => {
      const dialogRef = await AddProductComponent.open(this.dialog, this.billingStatusService, {
        origin: 'products',
        manufacturer: this.appService.manufacturer()!,
      });
      if (dialogRef == null) {
        return;
      }
      await firstValueFrom(dialogRef.afterClosed());
    },
  };

  CREATE_REVISION_ACTION: Action<AggregatedProduct> = {
    id: 'createRevision',
    scopes: ['contextMenu'],
    icon: 'note_add',
    labelMessageKey: 'products.createRevision',
    disabled: () => false,
    on: async (product?: AggregatedProduct) => {
      if (!product?.id) {
        return;
      }

      const dialogRef = this.dialog.open(AddRevisionComponent, {
        data: {
          productId: product.id,
        },
      });

      const result: { revision: ApiProductRevision; openRevisionAfterCreation: boolean } =
        await firstValueFrom(dialogRef.afterClosed());

      if (!result?.revision?.id) {
        return;
      }

      if (result.openRevisionAfterCreation) {
        await this.router.navigate([product.id, result.revision.id], {
          relativeTo: this.route.parent,
        });
      }
    },
  };

  SHOW_LATEST_REVISION_ACTION: Action<AggregatedProduct> = {
    id: 'showLatestRevision',
    scopes: ['contextMenu'],
    icon: 'open_in_new',
    labelMessageKey: 'products.openRevision',
    disabled: (product?: AggregatedProduct) => !product?.latestRevisionId,
    on: async (product?: AggregatedProduct) => {
      if (!product?.id || !product.latestRevisionId) {
        return;
      }

      await this.router.navigate([product.id, product.latestRevisionId], {
        relativeTo: this.route.parent,
      });
    },
  };

  override getActions(): Action<AggregatedProduct>[] {
    return [
      this.ADD_PRODUCT_ACTION,
      this.OPEN_ALL_ACTION,
      this.SHOW_LATEST_REVISION_ACTION,
      this.CREATE_REVISION_ACTION,
      ...super.getActions(),
    ];
  }

  async deleteByIds(ids: number[]): Promise<void> {
    await firstValueFrom(this.apiService.deleteProducts(ids));
    void this.billingStatusService.refresh();
  }

  private async openAllRevisions(id: number): Promise<void> {
    await this.router.navigate([id], { relativeTo: this.route.parent });
  }

  override getItemDisplayName(item: AggregatedProduct): string {
    return String(item.name);
  }
}
